import { getFirebaseStorage } from "@/lib/firebase";
import { ref, uploadBytesResumable, deleteObject } from "firebase/storage";

export interface AudioUploadOptions {
  /** Duration of the audio in seconds */
  duration?: number;
  /** Optional callback for upload progress (0-100) */
  onProgress?: (progress: number) => void;
}

export interface AudioUploadResult {
  storagePath: string;
  fileName: string;
}

/**
 * Get file extension from MIME type
 */
function getExtensionFromMimeType(mimeType: string): string {
  if (mimeType.includes("webm")) return "webm";
  if (mimeType.includes("mp4") || mimeType.includes("m4a")) return "m4a";
  if (mimeType.includes("mp3") || mimeType.includes("mpeg")) return "mp3";
  if (mimeType.includes("ogg")) return "ogg";
  if (mimeType.includes("wav")) return "wav";
  return "webm"; // Default fallback
}

/**
 * Upload audio blob to Firebase Storage with progress callback
 *
 * @param audioBlob - The audio blob to upload
 * @param userId - User ID for storage path
 * @param conversationId - Conversation ID for storage path
 * @param options - Upload options (duration, onProgress callback)
 * @returns Upload result with storage path and file name
 */
export async function uploadAudioToStorage(
  audioBlob: Blob,
  userId: string,
  conversationId: string,
  options?: AudioUploadOptions,
): Promise<AudioUploadResult> {
  const opts = options ?? {};

  const storage = getFirebaseStorage();

  const extension = getExtensionFromMimeType(audioBlob.type);
  // Audio files use timestamp for uniqueness (they have no meaningful original name)
  const fileName = `${Date.now()}_audio.${extension}`;
  const storagePath = `users/${userId}/projects/${conversationId}/audio/${fileName}`;

  const storageRef = ref(storage, storagePath);

  // Build custom metadata with duration if available
  const customMetadata: Record<string, string> = {};
  if (opts.duration !== undefined && opts.duration > 0) {
    customMetadata.duration = String(opts.duration);
  }

  return new Promise((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, audioBlob, {
      contentType: audioBlob.type,
      customMetadata:
        Object.keys(customMetadata).length > 0 ? customMetadata : undefined,
    });

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        opts.onProgress?.(Math.round(progress));
      },
      (error) => {
        reject(error);
      },
      () => {
        resolve({
          storagePath: `audio/${fileName}`,
          fileName,
        });
      }
    );
  });
}

/**
 * Delete audio file from Firebase Storage
 *
 * @param userId - User ID for storage path
 * @param conversationId - Conversation ID for storage path
 * @param fileName - The file name to delete
 */
export async function deleteAudioFromStorage(
  userId: string,
  conversationId: string,
  fileName: string,
): Promise<void> {
  const storage = getFirebaseStorage();
  const storagePath = `users/${userId}/projects/${conversationId}/audio/${fileName}`;
  const storageRef = ref(storage, storagePath);

  await deleteObject(storageRef);
}
